// @flow

import React from "react";
import L from "leaflet";
import "leaflet-fullscreen";
import "./leaflet-timeline-slider";
import * as rangeSlider from "./range-slider";
import { Row, Col, Card, CardBody, Collapse } from "shards-react";
import leafletPip from "./pip";
import leafletKnn from "./knn";
import * as utils from "../../utils/utils";
import "./leaflet.awesome-markers.min.js";
import "leaflet-easybutton";
import * as d3scale from "d3-scale";
import * as d3interpolate from "d3-interpolate";

const cookies = utils.cookies;

require("leaflet-spin");
require("./L.Modal.min");

const DEBUG_MODE = false;

const shareStyle = {
  marginBottom: "5px",
  textAlign: "left",
};

const redColor = {
  color: "red",
};

const greenColor = {
  color: "green",
};

const ZOOMED_OUT_STYLE = {
  stroke: true,
  weight: 1,
  fillOpacity: 0.9,
};
const ZOOMED_IN_STYLE = {
  stroke: true,
  weight: 3,
  fillOpacity: 0.5,
};

const MAX_ZOOM = 8;

function getConfirmedColor(d) {
  if (d == undefined || d == null) {
    return "black";
  }

  return (
    // d > 1000 ? '#800026' :
    d > 10000
      ? "#3C000D"
      : d > 1000
      ? "#80001B"
      : d > 100
      ? "#bd0026"
      : d > 50
      ? "#f03b20"
      : d > 20
      ? "#fd8d3c"
      : d > 5
      ? "#feb24c"
      : d > 1
      ? "#fed976"
      : "#ffffb2"
  );
}

let domainArray = [];
const colorsArray = [
  "#fff5bd",
  "#ffb500",
  "#ff7e00",
  "#f24b0d",
  "#cd311f",
  "#b10048",
  "#7c0864",
  "#4a185a",
  "#2b123e",
  "#140321",
];

// Create domains array equal to number of colors
// distribution of lightness/colors handled outside of this tool
for (let i = 0; i < colorsArray.length; i++) {
  let inc;
  if (i === 0) {
    inc = 0;
  } else {
    inc = i / colorsArray.length;
  }
  domainArray.push(inc);
}
domainArray = domainArray.sort();

const getInfectedColor = d3scale
  .scaleLinear()
  .domain(domainArray)
  .range(colorsArray)
  .interpolate(d3interpolate.interpolateHcl);

function getMiles(meters) {
  return meters * 0.000621371192;
}

export default class USMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showMap: true,
      loadingGeo: false,
      loadingData: true,
      geoBlocked: false,
      geoInaccurate: false,
      userLat: null,
      userLng: null,
      closestCircleUserIsIn: null,
      showPermissionInfo: false,
    };
    this.legendType = null;
  }

  setLegend(newLegendType) {
    // If no type given, just set the existing one.
    if (!newLegendType) {
      newLegendType = this.legendType;
    }

    if (newLegendType == "confirmedCount") {
      this.setConfirmedLegend();
    } else if (newLegendType == "infectedPerc") {
      this.setPerInfectedLegend();
    }
    this.legendType = newLegendType;
  }

  addTimelineSlider() {
    const mainThis = this;
    let daysToUse = this.props.ts
      .filter((d) => {
        return d.stateToCountyToData && d.date != "3/13";
      })
      .map((d) => d.date);
    const timelineItems = [...daysToUse, "Now"];

    const tooltip = document.createElement("div");
    tooltip.classList.add("rangeSlider_tooltip");

    L.control
      .timelineSlider({
        // timelineItems: [...daysToUse, 'Now'],
        // changeMap: ({ label, value, map }) => {
        // },
        // extraChangeMapParams: {},
        // initializeChange: false
      })
      .addTo(this.map);

    const slider = document.querySelector('input[type="range"]');
    let currPosition = timelineItems.length - 1; // +1 for Now as last element
    rangeSlider.create(slider, {
      polyfill: true, // Boolean, if true, custom markup will be created
      root: document,
      rangeClass: "rangeSlider",
      disabledClass: "rangeSlider--disabled",
      fillClass: "rangeSlider__fill",
      bufferClass: "rangeSlider__buffer",
      handleClass: "rangeSlider__handle",
      startEvent: ["mousedown", "touchstart", "pointerdown"],
      moveEvent: ["mousemove", "touchmove", "pointermove"],
      endEvent: ["mouseup", "touchend", "pointerup"],
      vertical: false, // Boolean, if true slider will be displayed in vertical orientation
      min: 0, // Number, 0
      max: currPosition, // Number, 100
      step: 1, // Number, 1
      value: currPosition, // Number, center of slider
      buffer: null, // Number, in percent, 0 by default
      stick: null, // [Number stickTo, Number stickRadius] : use it if handle should stick to ${stickTo}-th value in ${stickRadius}
      borderRadius: 10, // Number, if you're using buffer + border-radius in css
      // onInit: function () {
      // },
      // onSlideStart: function (position, value) {
      //   console.info('onSlideStart', 'position: ' + position, 'value: ' + value);
      // },
      onSlide: function (position, value) {
        mainThis.map.dragging.disable();
        // console.log(this)
        // TODO: make slider thumb cursor: pointer
        if (position != currPosition) {
          currPosition = position;

          const label = timelineItems[position];

          tooltip.textContent = label;
          // Update map
          if (label == "Now") {
            mainThis.updateMarkers(mainThis.props.data);
            return;
          }
          if (mainThis.props.ts) {
            let dateData = mainThis.props.ts.filter((e) => e.date == label);
            if (dateData && dateData.length > 0) {
              dateData = dateData[0];
              const stateToCountyToData = dateData.stateToCountyToData;
              if (!stateToCountyToData) return;

              Object.entries(stateToCountyToData).forEach(
                ([state, { stateConfirmed, stateDeaths, countyToData }]) => {
                  Object.entries(countyToData).forEach(
                    ([county, countyData]) => {
                      try {
                        const latestCountyData =
                          mainThis.props.data[state].countyToData[county];
                        countyData.geometry = latestCountyData.geometry;
                        countyData.pop = latestCountyData.pop;
                      } catch (e) {
                        console.error(e);
                      }
                    }
                  );
                }
              );

              mainThis.updateMarkers(stateToCountyToData);
            }
          }
        }
        mainThis.map.dragging.enable();
      },
      // onSlideEnd: function (position, value) {
      //   console.warn('onSlideEnd', 'position: ' + position, 'value: ' + value);
      // }
    });

    const rangeSliderEl = slider.rangeSlider;
    const handleEl = rangeSliderEl.handle;
    handleEl.appendChild(tooltip);
    tooltip.textContent = timelineItems[rangeSliderEl.value];

    // Hack to "half-fix" slider width bug
    const container = document.querySelector(".control_container");
    container.style.width = `${container.clientWidth}px`;

    this.timelineAdded = true;
  }

  resetLocation() {
    cookies.remove(utils.COOKIE_NAME);
    window.location.reload();
  }

  componentDidUpdate({ data, ts }) {
    // check if data has changed
    if (this.props.data !== data) {
      this.updateMarkers(this.props.data);
    }

    if (data && ts && !this.timelineAdded) {
      this.addTimelineSlider();
    }

    // Let parent know the county user is in
    if (this.inCounty) {
      this.props.setUserCounty(this.inCounty);
    }
  }

  displayStatus() {
    if (this.state.geoInaccurate) {
      return "Sorry, we couldn't accurately determine your location. Please try reloading the page, or interact with the map manually instead.";
    }
    if (this.state.geoBlocked) {
      return (
        <div>
          <div>
            <u>
              We don't have permission to detect your location so we can't check
              if you're in an infection zone
            </u>
            .{" "}
            <a
              href="javascript:void(0)"
              onClick={() => this.setState({ showPermissionInfo: true })}
            >
              Find out how to give permission
            </a>
            .
          </div>
          <Collapse open={this.state.showPermissionInfo}>
            <div>
              <b>If you would like to grant permission on iOS</b>, go to
              Settings -> General, scroll to the bottom and go to Reset -> Reset
              Location & Privacy, then reload the site.
            </div>
            <div>
              <b>On Android</b>, in Chrome tap the three dots to the right of
              the address bar, then Settings -> Site settings -> Location ->
              turn location on for the site.
            </div>
            <br />
          </Collapse>
          <div>
            You can also pinch and zoom the map manually to find your area.
          </div>
        </div>
      );
    }

    if (
      !this.state.loadingGeo &&
      (!this.state.userLat || !this.state.userLng)
    ) {
      return (
        <div>
          <button
            type="button"
            className="mb-2 btn btn-primary mr-2 autodetect-button"
            onClick={() => this.locateUser()}
          >
            Click to autodetect your location
          </button>
        </div>
      );
    }

    if (this.state.loadingGeo || this.state.loadingData) {
      return (
        <b style={redColor}>
          <i className="fas fa-sync fa-spin"></i>&nbsp;&nbsp;Checking if you're
          within an infection zone...
        </b>
      );
    }

    if (!this.inCounty && !this.nearestCounty) {
      try {
        const inCountyResult = leafletPip.pointInLayer(
          [this.state.userLng, this.state.userLat],
          this.geoLayer,
          true
        ); // pip uses lng,lat order; true to return first (still a list)
        if (inCountyResult && inCountyResult[0]) {
          const inCounty = inCountyResult[0].feature.properties;
          this.inCounty = inCounty;
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (this.inCounty) {
      if (!this.initialInfoUpdated) {
        this.info.update(this.inCounty);
        this.initialInfoUpdated = true;
      }

      let todayNewConfirmed = null;
      let todayNewDeaths = null;
      try {
        if (this.props.ts && this.props.ts.length > 1) {
          let latestTS = utils.getLast(this.props.ts);
          const latestCountyData =
            latestTS.stateToCountyToData[this.inCounty.state].countyToData[
              this.inCounty.county
            ];
          todayNewConfirmed =
            this.inCounty.confirmed - latestCountyData.countyConfirmed;
          todayNewDeaths = this.inCounty.deaths - latestCountyData.countyDeaths;
        }
      } catch (e) {
        console.log(
          "Couldn't find latest TS data for user county.",
          this.inCounty
        );
      }

      return (
        <div>
          <div>
            You are in the{" "}
            <b>
              <u>{this.inCounty.title}</u>
            </b>{" "}
            infection zone with{" "}
            <b className="confirmed-style">
              {this.inCounty.confirmed}{" "}
              {this.inCounty.confirmed == 1 ? "case" : "cases"}
            </b>
            * and{" "}
            <b className="deaths-style">
              {this.inCounty.deaths ? this.inCounty.deaths : 0}{" "}
              {this.inCounty.deaths == 1 ? "death" : "deaths"}
            </b>
            *.
          </div>
          {todayNewConfirmed != null || todayNewDeaths != null ? (
            <div>
              Today, there were{" "}
              <b className="confirmed-style">
                {todayNewConfirmed || 0} new{" "}
                {todayNewConfirmed == 1 ? "case" : "cases"}
              </b>
              * and{" "}
              <b className="deaths-style">
                {todayNewDeaths || 0} new{" "}
                {todayNewDeaths == 1 ? "death" : "deaths"}
              </b>
              * (resets midnight PDT).
            </div>
          ) : (
            ""
          )}
          <small>
            *Includes confirmed, probable, and presumptive positive cases.
          </small>
        </div>
      );
    }

    // find closest infected county to user

    if (!this.nearestCounty) {
      try {
        let nearestResult = leafletKnn(this.geoLayer)(
          L.latLng(this.state.userLat, this.state.userLng),
          1
        )[0];
        const {
          lon: nearestCountyLng,
          lat: nearestCountyLat,
          layer: nearestLayer,
        } = nearestResult;
        const distanceToUser = L.latLng(
          this.state.userLat,
          this.state.userLng
        ).distanceTo(L.latLng(nearestCountyLat, nearestCountyLng)); // meters
        const milesToUser = Math.round(getMiles(distanceToUser));
        this.distanceToShow =
          milesToUser <= 1 ? "less than 1 mile" : milesToUser + " miles";
        this.nearestCounty = nearestLayer.feature.properties;

        // pan to nearest county
        // warning: panning seems to compete with flyTo
        const nearestMarkerIcon = L.AwesomeMarkers.icon({
          prefix: "fa",
          icon: "biohazard",
          markerColor: "red",
        });
        var nearestMarker = L.marker([nearestCountyLat, nearestCountyLng], {
          icon: nearestMarkerIcon,
        }).bindPopup(
          `${this.nearestCounty.title} is the closest infection zone to you, ${this.distanceToShow} away.`
        );
        this.map.addLayer(nearestMarker);
        // nearestMarker.openPopup()
        var group = new L.featureGroup([this.userMarker, nearestMarker]);
        this.map.flyToBounds(group.getBounds(), { padding: [70, 70] });
        if (!this.initialInfoUpdated) {
          this.info.update(this.nearestCounty, this.distanceToShow);
          this.initialInfoUpdated = true;
        }

        // this.map.panInside(L.latLng(nearestCountyLat, nearestCountyLng))
      } catch (e) {
        console.error(e);
      }
    }

    return (
      <div>
        <span style={greenColor}>
          <b>You are not in a confirmed infection zone!</b>
        </span>{" "}
        {this.nearestCounty ? (
          <span>
            The closest infection zone to you is{" "}
            <span style={redColor}>{this.distanceToShow} away</span> in{" "}
            <span style={redColor}>{this.nearestCounty.title}</span> with{" "}
            <span className="confirmed-style">
              {this.nearestCounty.confirmed} confirmed{" "}
              {this.nearestCounty.confirmed == 1 ? "case" : "cases"}
            </span>
            .
          </span>
        ) : (
          ""
        )}
        <div>
          <b>Please note</b> this does not mean your immediate area is
          unaffected by the virus since testing is still limited and reporting
          may be delayed. Please check back regularly to stay up-to-date with
          your area's status.
        </div>
      </div>
    );
  }

  setLocation = (userLat, userLng) => {
    const userMarkerIcon = L.AwesomeMarkers.icon({
      prefix: "fa",
      icon: "user",
      markerColor: "blue",
    });

    this.userMarker = L.marker([userLat, userLng], {
      icon: userMarkerIcon,
    }).bindPopup("You are here.");

    this.map.addLayer(this.userMarker);

    this.map.flyTo([userLat, userLng], MAX_ZOOM, {
      // zoom/pan options
    });
    this.geoLayer.setStyle(ZOOMED_IN_STYLE);
    // this.map.fire('zoomend')

    cookies.set(
      utils.COOKIE_NAME,
      {
        userLat: userLat,
        userLng: userLng,
      },
      {
        path: "/",
        maxAge: 2592000, // 1 month in seconds
      }
    );

    this.setState({
      loadingGeo: false,
      userLat: userLat,
      userLng: userLng,
    });
  };

  locateUser = () => {
    console.log("Attempting to detect user location...");
    this.setState({
      loadingGeo: true,
      // Reset state
      geoInaccurate: false,
      geoBlocked: false,
    });
    this.map
      .locate({
        setView: false,
        maxZoom: MAX_ZOOM,
        timeout: 30000,
        maximumAge: 900000, // 15min cache of geolocation
      })
      .on("locationfound", (e) => {
        this.locationFound = true;
        // default to showing full map if accuracy is poor, 40miles+
        if (e.accuracy >= 64373) {
          console.log("User location found but inaccurate!");
          this.setState({
            loadingGeo: false,
            geoInaccurate: true,
          });
        } else {
          console.log("Found user location!");
          const [userLat, userLng] = [e.latitude, e.longitude];

          // Ventura (GeometryCollection)
          // const [userLat, userLng] = [34.343959, -119.106835]
          // Edward's Airforce Base
          // const [userLat, userLng] = [34.923537, -117.901230]
          // LA CSUN (GeometryCollection)
          // const [userLat, userLng] = [34.239585, -118.530352]
          // south dakota
          // const [userLat, userLng] = [43.327605, -102.443864]
          // montana
          // const [userLat, userLng] = [46.975656, -109.126640]
          // montana 2
          // const [userLat, userLng] = [48.468074, -108.507376]

          this.setLocation(userLat, userLng);
        }
      })
      .on("locationerror", (e) => {
        console.log("locationerror", e);
        if (e && e.message && e.message.includes("denied")) {
          this.setState({
            geoBlocked: true,
            loadingGeo: false,
          });
        } else {
          this.setState({
            geoInaccurate: true,
            loadingGeo: false,
          });
        }
      });
  };

  componentDidMount() {
    const mainThis = this;
    // TODO: change default to US zoomed out view
    this.centerLat = 47.608;
    this.centerLng = -122.3351;

    // create map
    this.map = L.map("usmap", {
      center: [49.8419, 24.0315],
      // center: [this.centerLat, this.centerLng],
      zoom: MAX_ZOOM,
      layers: [
        // L.tileLayer('https://{s}.tile.osm.org/{z}/{x}/{y}.png', {
        // L.tileLayer('https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/5/{x}/{y}.jpg70?access_token=pk.eyJ1IjoidXNhY29yb25hdmlydXN0cmFja2VyIiwiYSI6ImNrODVlMDljaTAxYmgzZWtnYXE5NjdjZ28ifQ.LrEvquTR4V7uBv0FBD4B3Q', {
        //   attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        // }),
        // L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}{r}?access_token={accessToken}', {
        //   // attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
        //   attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
        //   // maxZoom: 11,
        //   tileSize: 512,
        //   zoomOffset: -1,
        //   id: 'usacoronavirustracker/ck86ul69h0fp91jljfet1wr4n',
        //   accessToken: 'pk.eyJ1IjoidXNhY29yb25hdmlydXN0cmFja2VyIiwiYSI6ImNrODVlMDljaTAxYmgzZWtnYXE5NjdjZ28ifQ.LrEvquTR4V7uBv0FBD4B3Q'
        // })
        // L.tileLayer('https://tiles.infection2021.com/tiles/{z}/{x}/{y}.png', {
        L.tileLayer("https://infection2020.netlify.app/tiles/{z}/{x}/{y}.png", {
          maxZoom: MAX_ZOOM,
          attribution:
            '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap contributors</a> | infection2020.com',
        }),
      ],
      fullscreenControl: {
        pseudoFullscreen: true, // if true, fullscreen to page width and height
      },
    });

    this.map.fitBounds([
      [25.157094, -124.850693], //Southwest
      [47.942567, -63.682594], //Northeast
    ]);

    this.map.spin(true);

    // County outline layer
    this.geoLayer = L.geoJSON(null, {
      style: function (feature) {
        const confirmedCount = feature.properties.confirmed;
        let colorStyle;
        if (this.legendType == "confirmedCount") {
          const color = getConfirmedColor(confirmedCount);
          colorStyle = {
            color: color,
            fillColor: color,
          };
        } else if (this.legendType == "infectedPerc") {
          const infectedPerc = feature.properties.infectedPerc;
          const color = getInfectedColor(infectedPerc / this.maxInfectedPerc);
          colorStyle = {
            color: color,
            fillColor: color,
          };
        }

        let s = {
          opacity: 1,
          ...colorStyle,
          ...this.getZoomStyle(),
        };
        return s;
      }.bind(this),
      onEachFeature: function (feature, layer) {
        layer.on("click", (e) => {
          this.info.update(feature.properties);
        });
      }.bind(this),
    }).addTo(this.map);

    const cookie = cookies.get(utils.COOKIE_NAME);
    if (cookie && cookie.userLat && cookie.userLng) {
      this.setLocation(cookie.userLat, cookie.userLng);
    }

    // info box
    this.info = L.control();

    this.getZoomStyle = () => {
      const currentZoom = this.map.getZoom();
      if (currentZoom < 6) {
        return ZOOMED_OUT_STYLE;
      } else {
        return ZOOMED_IN_STYLE;
      }
    };

    this.map.on(
      "zoomend",
      function () {
        this.geoLayer.setStyle(this.getZoomStyle());
      }.bind(this)
    );

    const setPerInfectedLegendDOM = (color, n, container) => {
      const base = document.querySelector(container);
      base.innerHTML = "";

      const title = document.createElement("div");
      title.setAttribute("style", "margin-bottom:3px");
      title.innerHTML = "% of Population Infected";
      base.appendChild(title);

      const canvas = document.createElement("canvas");
      base.appendChild(canvas);
      canvas.setAttribute("width", `${n}px`);
      canvas.setAttribute("height", "16px");
      const context = canvas.getContext("2d");

      canvas.style.width = `${n}px`;
      canvas.style.height = "16px";
      canvas.style.imageRendering = "pixelated";

      for (let i = 0; i < n; ++i) {
        context.fillStyle = color(i / (n - 1));
        context.fillRect(i, 0, 1, 32);
      }

      const scaleText = document.createElement("div");
      const leftScaleLabel = document.createElement("label");
      leftScaleLabel.setAttribute(
        "style",
        "margin-bottom:0px; width:inherit; float:left;"
      );
      leftScaleLabel.innerHTML = `${this.minInfectedPerc}%`;
      const rightScaleLabel = document.createElement("label");
      rightScaleLabel.setAttribute(
        "style",
        "margin-bottom:0px; width:inherit; float:right"
      );
      rightScaleLabel.innerHTML = `${this.maxInfectedPerc}%`;
      scaleText.appendChild(leftScaleLabel);
      scaleText.appendChild(rightScaleLabel);
      base.appendChild(scaleText);
    };

    this.setPerInfectedLegend = () => {
      this.geoLayer.eachLayer((layer) => {
        const infectedPerc = layer.feature.properties.infectedPerc;
        const fillColor = getInfectedColor(infectedPerc / this.maxInfectedPerc);
        layer.setStyle({
          color: fillColor, // border color
          fillColor: fillColor,
        });
      });

      setPerInfectedLegendDOM(getInfectedColor, 240, ".legend");
    };

    this.updateMarkers(this.props.data);

    this.info.onAdd = function (map) {
      this._div = L.DomUtil.create("div", "info"); // create a div with a class "info"
      return this._div;
    };

    // method that we will use to update the control based on feature properties passed
    this.info.update = function (properties, distanceAway) {
      let output = "";
      if (properties && properties.title) {
        mainThis.infoBoxProps = properties;
        output = `<h6>${properties.title}</h6>`;
        if (distanceAway != undefined) {
          output += `<small>${distanceAway} away</small>`;
        }
        if (properties.pop) {
          output += `<div>Population: <span class="">${properties.pop.toLocaleString()}</span></div>`;
          const perInfectedStr = `(1 : ${properties.numPeoplePerInfected})`;
          output += `<div>Infected: <span class="deaths-style">${properties.infectedPerc}% ${perInfectedStr}</span></div>`;
        }
        output += `<div>Cases: <span class="deaths-style">${
          properties.confirmed || 0
        }</span></div>`;
        output += `<div>Deaths: <span class="deaths-style">${
          properties.deaths || 0
        }</span></div>`;
      } else {
        output = "<h6>Click on a zone</h6>";
      }
      this._div.innerHTML = output;
    };

    this.info.addTo(this.map);
    this.info.update();

    this.setConfirmedLegend = () => {
      const div = document.querySelector(".legend");

      const grades = [1, 2, 5, 20, 50, 100, 1000, 10000];
      const labels = [];

      div.innerHTML = "";
      div.innerHTML += "<div style='margin-bottom:3px;'>Confirmed Cases</div>";
      // loop through our density intervals and generate a label with a colored square for each interval
      // first loop for colored legend boxes
      for (var i = 0; i < grades.length; i++) {
        if (i == 0) {
          div.innerHTML +=
            '<span style="background:' +
            getConfirmedColor(grades[i]) +
            '"></span> ';
        } else {
          div.innerHTML +=
            '<span style="background:' +
            getConfirmedColor(grades[i] + 1) +
            '"></span> ';
        }
      }

      // a line break
      div.innerHTML += "<br>";

      div.innerHTML += '<label style="margin-bottom:0px">1</label>';
      div.innerHTML += '<label style="margin-bottom:0px">2+</label>';
      div.innerHTML += '<label style="margin-bottom:0px">6+</label>';
      div.innerHTML += '<label style="margin-bottom:0px">21+</label>';
      div.innerHTML += '<label style="margin-bottom:0px">51+</label>';
      div.innerHTML += '<label style="margin-bottom:0px">101+</label>';
      div.innerHTML += '<label style="margin-bottom:0px">1K+</label>';
      div.innerHTML += '<label style="margin-bottom:0px">10K+</label>';

      this.geoLayer.eachLayer((layer) => {
        const confirmed = layer.feature.properties.confirmed;
        const fillColor = getConfirmedColor(confirmed);
        layer.setStyle({
          color: fillColor,
          fillColor: fillColor,
        });
      });
    };

    // Add legend
    const legend = L.control({ position: "bottomright" });
    legend.onAdd = function (map) {
      const div = L.DomUtil.create("div", "legend");
      return div;
    };
    legend.addTo(this.map);
    this.setLegend("confirmedCount");

    const modalButton = L.control({ position: "bottomright" });
    modalButton.onAdd = (map) => {
      const div = document.createElement("div");
      div.className = "leaflet-bar cog-control";
      div.onclick = () => {
        map.fire("modal", {
          content: `
            <div style="text-align:center; padding:10px 0 0 0;">
              <span class="stats-small__label text-uppercase" style="font-size:0.75rem;">Choose Legend</span>
              <div id="legend-btn-group" style="padding: 5px 0">
                <div id="confirmedButton"><button class="mb-2 mr-1 btn btn-outline-primary modal-inner-btn"># of Confirmed Cases</button></div>
                <div id="infectedButton"><button class="mb-2 mr-1 btn btn-outline-primary modal-inner-btn">% of Population Infected</button></div>
              </div>
            </div>
          `,
          onShow: (evt) => {
            var modal = evt.modal;
            L.DomEvent.on(
              modal._container.querySelector("#confirmedButton"),
              "click",
              () => {
                modal._container.querySelector(
                  "#legend-btn-group"
                ).style.display = "none";
                modal.hide();
                // mainThis.setConfirmedLegend()
                this.setLegend("confirmedCount");
              }
            ).on(
              modal._container.querySelector("#infectedButton"),
              "click",
              () => {
                modal._container.querySelector(
                  "#legend-btn-group"
                ).style.display = "none";
                modal.hide();
                // mainThis.setPerInfectedLegend()
                this.setLegend("infectedPerc");
              }
            );
          },
          onHide: (evt) => {
            var modal = evt.modal;
            modal._container.querySelector("#legend-btn-group").style.display =
              "none";
          },

          // closeTitle: 'close',                 // alt title of the close button
          // zIndex: 10000,                       // needs to stay on top of the things
          // transitionDuration: 300,             // expected transition duration

          // // template: '{contint}',               // modal body template, this doesn't include close button and wrappers

          // // callbacks for convenience,
          // // you can set up you handlers here for the contents
          // // onShow: function(evt){ var modal = evt.modal; ...},
          // // onHide: function(evt){ var modal = evt.modal; ...},

          // // change at your own risk
          OVERLAY_CLS: "overlay", // overlay(backdrop) CSS class
          MODAL_CLS: "modal", // all modal blocks wrapper CSS class
          MODAL_CONTENT_CLS: "modal-content", // modal window CSS class
          INNER_CONTENT_CLS: "modal-inner", // inner content wrapper
          SHOW_CLS: "show", // `modal open` CSS class, here go your transitions
          CLOSE_CLS: "close", // `x` button CSS class
        });
      };

      div.innerHTML +=
        '<span class="legend-btn-inner"><i class="fas fa-cog"></i> Change Legend</span>';
      return div;
    };
    modalButton.addTo(this.map);
  }

  addCountyOutlines(data) {
    let countMissing = 0;
    let countTotal = 0;
    let newInfoBoxProps;

    // let countyConfirmedHistogram = {}
    Object.entries(data).forEach(
      ([state, { stateConfirmed, stateDeaths, stateNew, countyToData }]) => {
        Object.entries(countyToData).forEach(([county, countyData]) => {
          countTotal += 1;
          // if (!countyConfirmedHistogram[countyData.countyConfirmed]) {
          //   countyConfirmedHistogram[countyData.countyConfirmed] = 1
          // } else {
          //   countyConfirmedHistogram[countyData.countyConfirmed] += 1
          // }

          // TODO: handle missing geometry cases
          if (!countyData.geometry) {
            if (DEBUG_MODE) {
              console.log(
                `Missing geo data for county ${county} in state ${state}`
              );
              console.log(countyData);
            }
            countMissing += 1;
            return;
          }

          const properties = {
            title: `${county}, ${state}`,
            county,
            state,
            confirmed: countyData.countyConfirmed,
            deaths: countyData.countyDeaths,
            new: countyData.countyNew,
            pop: countyData.pop,
          };

          const newFeature = {
            type: "Feature",
            geometry: countyData.geometry,
            properties,
          };

          if (properties.pop) {
            const numPeoplePerInfected = (
              properties.pop / properties.confirmed
            ).toFixed(0);
            const numDigits = numPeoplePerInfected.toString().length;
            // Display up to 2 significant decimal digits
            const percToShow = (
              (properties.confirmed / properties.pop) *
              100
            ).toFixed(numDigits - 1);
            properties.infectedPerc = parseFloat(percToShow);
            properties.numPeoplePerInfected = parseInt(numPeoplePerInfected);

            // set min/max seen values
            if (
              !this.minInfectedPerc ||
              properties.infectedPerc < this.minInfectedPerc
            ) {
              this.minInfectedPerc = properties.infectedPerc;
            }

            if (
              !this.maxInfectedPerc ||
              properties.infectedPerc > this.maxInfectedPerc
            ) {
              this.maxInfectedPerc = properties.infectedPerc;
            }
          }

          // Update info box on timeline change
          if (this.infoBoxProps) {
            let infoBoxState;
            let infoBoxCounty;
            infoBoxState = this.infoBoxProps.state;
            infoBoxCounty = this.infoBoxProps.county;
            if (
              properties.county == infoBoxCounty &&
              properties.state == infoBoxState
            ) {
              newInfoBoxProps = properties;
            }
          }

          this.geoLayer.addData(newFeature);
        });
      }
    );

    if (countTotal > 0) {
      this.map.spin(false);
      this.setState({
        loadingData: false,
      });
    }
    if (DEBUG_MODE) {
      console.log("missing: " + countMissing);
      console.log("total: " + countTotal);
    }

    if (this.infoBoxProps) {
      if (newInfoBoxProps) {
        this.info.update(newInfoBoxProps);
      } else {
        // console.log(`Info box: could not find data for ${this.infoBoxProps.county}, ${this.infoBoxProps.state}`)
        this.info.update();
      }
    }
  }

  updateMarkers(data) {
    if (!data) return;
    this.geoLayer.clearLayers();
    this.addCountyOutlines(data);
  }

  render() {
    return (
      <div>
        <h5>
          <i className="fas fa-biohazard"></i> Infection Map{" "}
          <a
            href="javascript:void(0)"
            onClick={() =>
              this.setState((state, props) => {
                return { showMap: !state.showMap };
              })
            }
          >
            {this.state.showMap ? "(hide)" : "(unhide)"}
          </a>
        </h5>
        <Collapse open={this.state.showMap}>
          {/*<utils.TimeDifference oldEpoch={this.props.updateEpoch} />*/}
          <div className="us-map-status">{this.displayStatus()}</div>
          {!this.state.loadingData &&
          !this.state.loadingGeo &&
          this.state.userLat &&
          this.state.userLng ? (
            <div>
              <a href="javascript:void(0)" onClick={() => this.resetLocation()}>
                (click to reset location)
              </a>
            </div>
          ) : (
            ""
          )}
          <div className="us-map-inform-text">
            Inform your friends & family:{" "}
            <span
              style={shareStyle}
              className="sharethis-inline-share-buttons"
            ></span>
          </div>
          <Row>
            <Col lg="12" md="12" sm="12" className="mb-4">
              <Card small className="">
                <CardBody className="p-0">
                  <div id="usmap" className="us-map disable-select"></div>
                </CardBody>
              </Card>
              {/* <div style={{marginTop: '5px'}}>Coming soon: new option to view colors based on % of people infected in your county.</div> */}
            </Col>
          </Row>
        </Collapse>
      </div>
    );
  }
}
